// MTT-Bausteine der Produktseite (Horizon). Stand 10.10.2026.
// Jeder Baustein ist ein eigenes Element mit data-block. Beim Variantenwechsel liefert Horizon
// das neue Seiten-HTML im productSelect-Ereignis; der Baustein übernimmt daraus seinen eigenen
// neuen Inhalt (gleiches data-block) und lädt danach seine Daten neu.
import { StandardEvents } from '@shopify/events';

class MttVariantBlock extends HTMLElement {
  connectedCallback() {
    this.abschnitt = this.closest('.shopify-section, dialog');
    this.abschnitt?.addEventListener(StandardEvents.productSelect, this.beiWechsel);
    this.aktualisiert();
  }
  disconnectedCallback() {
    this.abschnitt?.removeEventListener(StandardEvents.productSelect, this.beiWechsel);
  }
  beiWechsel = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    event.promise
      .then(({ detail }) => {
        const neu = detail?.html?.querySelector(`${this.localName}[data-block="${this.dataset.block}"]`);
        if (!neu) return;
        for (const [k, v] of Object.entries(neu.dataset)) this.dataset[k] = v;
        this.innerHTML = neu.innerHTML;
        this.aktualisiert();
      })
      .catch((e) => { if (e?.name !== 'AbortError') console.warn('[mtt]', e); });
  };
  aktualisiert() {}
}

// Lieferanzeige: grün „Auf Lager“, orange „Vorbestellung“, grau „Ausverkauft“, darunter EINE Lieferzeile
// aus der Lieferzeit-Funktion mtt-delivery (Memory feedback_lieferzeit_eine_quelle).
const EP = 'https://awpaatubmbqjekoiktxd.supabase.co/functions/v1/mtt-delivery';
const TAG = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const kurz = (iso) => { const d = new Date(iso + 'T12:00:00'); return `${TAG[d.getDay()]}, ${d.getDate()}.${d.getMonth() + 1}.`; };
const plusTage = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

class MttLieferanzeige extends MttVariantBlock {
  aktualisiert() {
    const stufe = this.dataset.stufe;
    this.schnellkauf(stufe === 'orange');
    if (stufe === 'grau') return;
    const zeile = this.querySelector('[data-zeile]');
    if (!zeile || this.dataset.geladen === this.dataset.variant) return;
    this.dataset.geladen = this.dataset.variant;
    // Seit 11.10.2026 (AP3): Daten aus Shopify (mtt.liefer_*) und den Lieferregeln, keine Abfrage mehr.
    // Rechnung in assets/mtt-lieferdatum.js. Ohne Feld am Artikel: bisheriger Weg über mtt-delivery (Übergang).
    const regelnEl = this.querySelector('script[data-regeln]');
    if (this.dataset.quelle === 'shopify' && regelnEl && window.MTTLiefer) {
      try {
        const r = JSON.parse(regelnEl.textContent), L = window.MTTLiefer, st = this.dataset.status;
        // 01.11.2026: Einlagerung bei Packstar (Feld mtt.liefer_einlagerung): festes grünes Fenster bis zum Umschalttag
        const eu = this.dataset.einUm, ev = this.dataset.einVon, eb = this.dataset.einBis;
        if (eu && ev && eb && L.iso(new Date()) < eu) {
          zeile.innerHTML = `Lieferung zwischen <b>${L.kurz(ev)}</b> und <b>${L.kurz(eb)}</b>`;
          zeile.dataset.von = 'einlagerung';
          return;
        }
        if (st === 'vorbestellung' || st === 'set_teil_vorbestellt') {
          this.vorbestellenKnopf();
          // 25.10.2026: Druck-Zuschlag (z. B. Thermo-Becher +5 Werktage) auch auf das Vorbestell-Datum
          const bis = L.vorbestellBis ? L.vorbestellBis(r, this.dataset.bis || L.plusTage(7 * (r.ohneTerminWochen || 10)), this.dataset.sku) : (this.dataset.bis || L.plusTage(7 * (r.ohneTerminWochen || 10)));
          zeile.innerHTML = `Lieferung bis spätestens <b>${L.kurz(bis)}</b>`;
        } else {
          // Lagerware: immer das Fenster (Johns Vorgabe 25.10.2026: nie „bis spätestens“), Druck-Zuschlag je SKU aus den Lieferregeln
          const f = L.fenster(r, this.dataset.land, this.dataset.eigen === '1', undefined, parseInt(this.dataset.extra || '0', 10) || 0, L.druckTage ? L.druckTage(r, this.dataset.sku) : 0);
          zeile.innerHTML = `Lieferung zwischen <b>${L.kurz(f.von)}</b> und <b>${L.kurz(f.bis)}</b>`;
        }
        zeile.dataset.von = this.dataset.status === 'lager' ? 'regeln' : 'feld';
        return;
      } catch (e) { console.warn('[mtt] Lieferregeln', e); }
    }
    fetch(`${EP}?product_id=${this.dataset.product}&variant_id=${this.dataset.variant}&vorbestellung=${stufe === 'orange' ? 1 : 0}`)
      .then((r) => r.json())
      .then((d) => {
        const teile = d.out_components && d.out_components.length;
        if (d.preorder || stufe === 'orange') {
          // Vorbestellung: nur das späteste Datum. Ohne Eintrag: heute + 10 Wochen (Johns Entscheid 04.10., Fall 3)
          const bis = d.preorder && d.max_date ? d.max_date : plusTage(70);
          if (stufe === 'gruen') {
            // Set mit vorbestelltem Teil: Anzeige und Knopf auf Vorbestellung umstellen
            this.dataset.stufe = 'orange';
            this.querySelector('[data-text]').textContent = teile ? 'Ein Teil dieses Sets ist vorbestellt' : 'Nicht auf Lager · Vorbestellung möglich';
            this.vorbestellenKnopf();
          }
          zeile.innerHTML = `Lieferung bis spätestens <b>${kurz(bis)}</b>`;
        } else {
          zeile.innerHTML = `Lieferung zwischen <b>${kurz(d.min_date)}</b> und <b>${kurz(d.max_date)}</b>`;
        }
      })
      .catch(() => { zeile.closest('.mtt-lz__zeile')?.setAttribute('hidden', ''); });
  }
  // Haupt-Knopf und Kaufleiste auf „Vorbestellen“; in der Kaufleiste nur den Text-Span, sonst fehlt Horizons
  // Mengenanzeige (quantityDisplay); der Haupt-Knopf hat den Text als nackten Textknoten
  vorbestellenKnopf() {
    this.abschnitt?.querySelectorAll('[ref="addToCartButton"]').forEach((b) => {
      const c = b.querySelector('.add-to-cart-text__content > span:first-child:not([ref])');
      if (c) { c.textContent = 'Vorbestellen'; return; }
      const t = [...b.childNodes].find((n) => n.nodeType === 3 && n.nodeValue.trim());
      if (t) t.nodeValue = ' Vorbestellen ';
    });
    this.schnellkauf(true);
  }
  // Johns Vorgabe 06.08./05.10.: bei Vorbestellung kein PayPal-/Schnellkauf, er überspringt Warenkorb und Versandwahl
  schnellkauf(aus) {
    const box = this.abschnitt?.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
    if (!box) return;
    // nach Horizons eigener Umschaltung setzen
    setTimeout(() => { if (aus) box.setAttribute('hidden', 'true'); }, 60);
  }
}

// „Informier mich, sobald verfügbar“ über Klaviyo Back-in-Stock (nur bei Vorbestellung sichtbar)
class MttInformierMich extends MttVariantBlock {
  aktualisiert() {
    const knopf = this.querySelector('[data-auf]');
    const feld = this.querySelector('[data-feld]');
    const senden = this.querySelector('[data-senden]');
    const status = this.querySelector('[data-status]');
    if (!knopf) return;
    knopf.addEventListener('click', () => { feld.hidden = !feld.hidden; if (!feld.hidden) feld.querySelector('input').focus(); });
    senden.addEventListener('click', () => {
      const input = feld.querySelector('input');
      const email = (input.value || '').trim();
      status.hidden = false;
      if (!email || email.indexOf('@') < 1) { status.textContent = 'Bitte eine gültige E-Mail-Adresse eingeben.'; return; }
      senden.disabled = true;
      status.textContent = 'Einen Moment …';
      fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=SqQQge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify({ data: { type: 'back-in-stock-subscription',
          attributes: { profile: { data: { type: 'profile', attributes: { email } } }, channels: ['EMAIL'] },
          relationships: { variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + this.dataset.variant } } } } }),
      })
        .then((r) => {
          if (r.ok) { status.textContent = 'Eingetragen! Wir melden uns, sobald der Artikel wieder verfügbar ist.'; input.disabled = true; }
          else { status.textContent = 'Das hat leider nicht geklappt. Bitte versuch es später erneut.'; senden.disabled = false; }
        })
        .catch(() => { status.textContent = 'Das hat leider nicht geklappt. Bitte versuch es später erneut.'; senden.disabled = false; });
    });
  }
}

if (!customElements.get('mtt-lieferanzeige')) customElements.define('mtt-lieferanzeige', MttLieferanzeige);
class MttSetInhalt extends MttVariantBlock {}
if (!customElements.get('mtt-set-inhalt')) customElements.define('mtt-set-inhalt', MttSetInhalt);
if (!customElements.get('mtt-informier-mich')) customElements.define('mtt-informier-mich', MttInformierMich);
